import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import Reveal from "../components/Reveal";
import { useI18n } from "../i18n/I18nProvider";

export default function PortfolioTeaser({ limit = 6 }) {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/portfolio").then((r) => { if (!cancelled) setItems(r.data || []); }).catch(() => setErr(true));
    return () => { cancelled = true; };
  }, []);

  const shown = items.slice(0, limit);

  return (
    <section id="portfolio" className="py-32 lg:py-40 bg-[#070708]" data-testid="home-portfolio">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12 lg:mb-16 items-end">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase text-ink-300">
                <span className="block w-8 h-px bg-rouge" /><span>{t.portfolio.eyebrow}</span>
              </div>
              <h2 className="mt-6 font-serif-editorial text-4xl sm:text-5xl lg:text-6xl text-ink-100 tracking-tight leading-[1.05] max-w-2xl">
                {t.portfolio.title}
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5 flex lg:justify-end">
            <Reveal>
              <Link
                to="/portfolio"
                data-testid="portfolio-view-all"
                data-cursor={t.portfolio.view_all}
                className="group inline-flex items-center gap-3 text-[11px] tracking-[0.24em] uppercase text-ink-100 border-b border-ink-500 hover:border-rouge pb-2 transition-colors"
              >
                {t.portfolio.view_all}
                <ArrowRight size={14} className="text-rouge transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>

        {err && <div className="text-ink-500 text-sm" data-testid="portfolio-error">Failed to load portfolio.</div>}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4 lg:gap-6">
          {shown.map((p, i) => {
            // Asymmetric Bento layout
            const layouts = [
              "lg:col-span-7 aspect-[16/10]",
              "lg:col-span-5 aspect-[4/5]",
              "lg:col-span-4 aspect-[4/5]",
              "lg:col-span-4 aspect-[4/5]",
              "lg:col-span-4 aspect-[4/5]",
              "lg:col-span-12 aspect-[21/9]",
            ];
            return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className={`group relative overflow-hidden bg-[#0c0c0e] ${layouts[i % layouts.length]}`}
                data-testid={`portfolio-tile-${i}`}
                data-cursor={p.category.toUpperCase()}
              >
                <img
                  src={p.cover}
                  alt={lang === "fr" ? p.title_fr : p.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-all duration-[1200ms] ease-out group-hover:scale-[1.04] saturate-50 group-hover:saturate-100"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
                <div className="absolute inset-0 p-6 lg:p-8 flex flex-col justify-end">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-ink-300/90 mb-2">
                    {p.category} · {p.year}
                  </div>
                  <h3 className="font-serif-editorial text-2xl lg:text-3xl text-ink-100 leading-tight">
                    {lang === "fr" ? p.title_fr : p.title}
                  </h3>
                </div>
                <div className="absolute top-4 right-4 w-8 h-8 border border-ink-100/30 group-hover:border-rouge transition-colors" aria-hidden />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
